import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';
import HamburgerMenu from './HamburgerMenu';
import { useAuth } from './useAuth';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
        status: {
            UP: '#28a745',
            XC: '#20c997',
            DN: '#dc3545',
            DS: '#dc3545',
            PM: '#ffc107',
            FCF: '#8716bbff',
            AWP: '#335d80ff',
            OTH: '#6c757d',
            DET: '#6c757d',
            AIMD: '#6c757d',
            ACI: '#6c757d',
            REI: '#6c757d'
        }
    };

    return {
        page: { backgroundColor: colors.bg, minHeight: '100vh', padding: '20px' },
        container: { maxWidth: '1600px', margin: '0 auto', fontFamily: 'sans-serif' },
        pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
        pageTitle: { fontSize: '24px', margin: 0, color: colors.text },
        header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
        tableContainer: {
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            padding: '20px',
            boxShadow: `0 2px 4px ${colors.shadow}`,
            overflowX: 'auto'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px'
        },
        tableHeader: {
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa',
            borderBottom: `2px solid ${colors.border}`
        },
        tableHeaderCell: {
            padding: '12px',
            textAlign: 'left',
            fontWeight: 'bold',
            color: colors.text,
            borderBottom: `2px solid ${colors.border}`
        },
        tableRow: {
            borderBottom: `1px solid ${colors.border}`,
            '&:hover': {
                backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa'
            }
        },
        tableCell: {
            padding: '12px',
            color: colors.text,
            borderBottom: `1px solid ${colors.border}`
        },
        buno: {
            fontWeight: 'bold',
            color: colors.link
        },
        statusBadge: {
            display: 'inline-block',
            padding: '4px 8px',
            borderRadius: '4px',
            color: 'white',
            fontSize: '12px',
            fontWeight: 'bold'
        },
        siteBadge: {
            display: 'inline-block',
            padding: '4px 8px',
            borderRadius: '4px',
            backgroundColor: isDark ? '#444' : '#e9ecef',
            color: colors.text,
            fontSize: '12px',
            fontWeight: 'bold'
        },
        noData: {
            textAlign: 'center',
            padding: '40px',
            color: colors.subtleText,
            fontSize: '16px'
        },
        controlsContainer: {
            display: 'flex',
            gap: '10px',
            marginBottom: '20px',
            flexWrap: 'wrap',
            alignItems: 'center'
        },
        searchInput: {
            padding: '8px 12px',
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            backgroundColor: isDark ? '#2a2a2a' : '#fff',
            color: colors.text,
            fontSize: '14px',
            minWidth: '200px'
        },
        filterSelect: {
            padding: '8px 12px',
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            backgroundColor: isDark ? '#2a2a2a' : '#fff',
            color: colors.text,
            fontSize: '14px',
            cursor: 'pointer'
        },
        status: colors.status,
        summaryTableContainer: {
            display: 'flex',
            gap: '20px',
            alignItems: 'flex-start'
        },
        summaryTable: {
            flex: '0 0 350px',
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            padding: '20px',
            boxShadow: `0 2px 4px ${colors.shadow}`
        },
        summaryTableHeader: {
            backgroundColor: isDark ? '#2a2a2a' : '#343a40',
            color: '#fff',
            padding: '12px',
            textAlign: 'left',
            fontWeight: 'bold',
            fontSize: '14px'
        },
        summaryTableRow: {
            borderBottom: `1px solid ${colors.border}`
        },
        summaryTableCell: {
            padding: '12px',
            color: colors.text,
            fontSize: '14px',
            borderBottom: `1px solid ${colors.border}`
        },
        bandA: { backgroundColor: '#28a745', color: '#fff' },
        bandB: { backgroundColor: '#90ee90', color: '#000' },
        bandC: { backgroundColor: '#ffc107', color: '#000' },
        bandD: { backgroundColor: '#ff9800', color: '#fff' }
    };
};

// Helper function to get current quarter start date
const getCurrentQuarterStart = () => {
    const now = new Date();
    const month = now.getMonth();
    const quarter = Math.floor(month / 3);
    return new Date(now.getFullYear(), quarter * 3, 1);
};

// Helper function to get current option year start year (Apr-Mar cycle)
const getCurrentOptionYear = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0-based
    return month >= 3 ? year : year - 1;
};

function SLMMPage({ theme }) {
    const styles = getStyles(theme);
    const isDark = theme === 'dark';
    const [aircraftList, setAircraftList] = useState([]);
    const [qtdAcTimeData, setQtdAcTimeData] = useState([]);
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const [siteFilter, setSiteFilter] = useState('');
    const [exemptStatus, setExemptStatus] = useState({});
    const [exemptDaysByBuno, setExemptDaysByBuno] = useState({});
    const navigate = useNavigate();
    const { isStaff, groups } = useAuth();

    // Fetch exempt status from aircraft status data
    useEffect(() => {
        if (aircraftList.length > 0) {
            // Build exempt status map from aircraft status data
            // The API returns data with buno, aco_exempt_status directly on each item
            const statusMap = {};
            aircraftList.forEach(aircraft => {
                if (aircraft.aco_exempt_status) {
                    statusMap[aircraft.buno] = aircraft.aco_exempt_status;
                } else {
                    // Default to 'MC' if not set
                    statusMap[aircraft.buno] = 'MC';
                }
            });
            setExemptStatus(statusMap);
        }
    }, [aircraftList]);

    // Fetch option-year exempt day totals by aircraft
    useEffect(() => {
        const fetchExemptDays = async () => {
            if (aircraftList.length === 0) {
                setExemptDaysByBuno({});
                return;
            }

            const optionYear = getCurrentOptionYear();
            const pageSize = 500;
            let page = 1;
            let allResults = [];
            let totalCount = 0;

            try {
                do {
                    const response = await api.get('/aircraft-status/slmm-exempt-days/', {
                        params: {
                            option_year: optionYear,
                            page,
                            page_size: pageSize
                        }
                    });

                    const payload = response.data || {};
                    const results = Array.isArray(payload.results) ? payload.results : [];
                    totalCount = payload.count || 0;
                    allResults = allResults.concat(results);
                    page += 1;
                } while (allResults.length < totalCount);

                const daysMap = {};
                allResults.forEach(item => {
                    if (item && item.buno) {
                        daysMap[item.buno] = {
                            exemptDays: Number(item.exempt_days) || 0,
                            meets180DayRule: Boolean(item.meets_180_day_rule),
                            historyGapAssumed: Boolean(item.history_gap_assumed)
                        };
                    }
                });
                setExemptDaysByBuno(daysMap);
            } catch (err) {
                console.error('Error fetching SLMM exempt day totals:', err);
                setExemptDaysByBuno({});
            }
        };

        fetchExemptDays();
    }, [aircraftList]);

    // Handle exempt status change - save to backend API
    const handleExemptStatusChange = async (buno, status) => {
        try {
            // Find the aircraft status record
            const aircraft = aircraftList.find(a => a.buno === buno);
            if (!aircraft) {
                console.error('Aircraft not found for BUNO:', buno);
                setAlert({ 
                    show: true, 
                    message: `Aircraft ${buno} not found.`, 
                    type: 'error' 
                });
                return;
            }

            // The primary key for AircraftStatus is 'aircraft' which is the BUNO
            // The API response has 'aircraft' field which is the BUNO string
            const aircraftId = aircraft.aircraft || aircraft.buno;
            
            console.log('Updating ACO Exempt Status:', { buno, status, aircraftId });
            
            // Update via API using the aircraft BUNO as the ID
            const response = await api.patch(`/aircraft-status/${aircraftId}/`, {
                aco_exempt_status: status
            });

            console.log('Update response:', response.data);

            // Update local state immediately for better UX
            setExemptStatus(prev => ({
                ...prev,
                [buno]: status
            }));

            // Update the aircraft in the list with the new status
            setAircraftList(prevList => 
                prevList.map(a => 
                    a.buno === buno 
                        ? { ...a, aco_exempt_status: status }
                        : a
                )
            );
        } catch (err) {
            console.error('Error updating ACO Exempt Status:', err);
            console.error('Error details:', err.response?.data);
            setAlert({ 
                show: true, 
                message: `Failed to update ACO Exempt Status for ${buno}. ${err.response?.data?.detail || err.message}`, 
                type: 'error' 
            });
            // Revert local state on error
            const aircraft = aircraftList.find(a => a.buno === buno);
            if (aircraft && aircraft.aco_exempt_status) {
                setExemptStatus(prev => ({
                    ...prev,
                    [buno]: aircraft.aco_exempt_status
                }));
            }
        }
    };

    // Fetch aircraft status data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [aircraftRes, qtdRes, sitesRes] = await Promise.all([
                    api.get('/aircraft-status/'),
                    api.get('/qtd-ac-time/'),
                    api.get('/sites/')
                ]);

                setAircraftList(aircraftRes.data || []);
                setQtdAcTimeData(qtdRes.data || []);
                setSites(sitesRes.data || []);
            } catch (err) {
                console.error('Error fetching data:', err);
                if (err.response && err.response.status === 401) {
                    navigate('/login');
                } else {
                    setAlert({ show: true, message: 'Failed to load data. Please try again.', type: 'error' });
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [navigate]);

    // Filter sites based on user's groups
    const filteredSites = useMemo(() => {
        if (!Array.isArray(sites)) return [];
        if (isStaff) {
            return sites;
        }
        return sites.filter(site => groups.includes(site.name));
    }, [sites, isStaff, groups]);

    // Auto-filter by site if user has exactly one site group policy
    useEffect(() => {
        if (!isStaff && groups.length > 0 && Array.isArray(sites) && sites.length > 0) {
            const siteGroups = groups.filter((group) =>
                sites.some((site) => site.name === group)
            );
            if (siteGroups.length === 1 && siteFilter === '') {
                setSiteFilter(siteGroups[0]);
            }
        }
    }, [groups, isStaff, siteFilter, sites]);

    // Calculate cumulative hours for each aircraft in current quarter
    const slmmData = useMemo(() => {
        const quarterStart = getCurrentQuarterStart();
        
        return aircraftList.map(aircraft => {
            // Find matching QTDACTime records for this aircraft
            // Match by site and s_serno (which is the BUNO)
            const matchingQtdRecords = qtdAcTimeData.filter(qtd => {
                if (!qtd.s_serno) return false;
                
                // Match site (case-insensitive)
                const siteMatch = qtd.site_name && aircraft.site && 
                    qtd.site_name.toUpperCase() === aircraft.site.toUpperCase();
                
                // Match BUNO (s_serno is the BUNO)
                const bunoMatch = qtd.s_serno.toUpperCase() === aircraft.buno.toUpperCase();
                
                return siteMatch && bunoMatch;
            });

            // Debug: Log matching records for first aircraft
            if (aircraftList.length > 0 && aircraft.buno === aircraftList[0].buno) {
                console.log('Aircraft:', aircraft.buno);
                console.log('Site:', aircraft.site);
                console.log('Matching QTD Records:', matchingQtdRecords.length);
                if (matchingQtdRecords.length > 0) {
                    console.log('Sample QTD Record:', matchingQtdRecords[0]);
                    console.log('QTD s_serno:', matchingQtdRecords[0].s_serno);
                    console.log('QTD dc_acft_flthrs:', matchingQtdRecords[0].dc_acft_flthrs);
                }
            }

            // Calculate cumulative hours for current quarter
            // Get the latest record before quarter start (baseline)
            const baselineRecord = matchingQtdRecords
                .filter(qtd => {
                    if (!qtd.uploaded_at) return false;
                    const uploadDate = new Date(qtd.uploaded_at);
                    return uploadDate < quarterStart;
                })
                .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at))[0];
            
            const baselineHours = baselineRecord ? (parseFloat(baselineRecord.dc_acft_flthrs) || 0) : 0;

            // Get the latest record in the current quarter
            const quarterRecords = matchingQtdRecords
                .filter(qtd => {
                    if (!qtd.uploaded_at) return false;
                    const uploadDate = new Date(qtd.uploaded_at);
                    return uploadDate >= quarterStart;
                })
                .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
            
            // If we have records in the quarter, use the latest one
            // Otherwise, if we have a baseline, the hours in quarter would be 0
            // If no records at all, try summing all records as incremental hours
            let cumulativeHours = 0;
            
            if (quarterRecords.length > 0) {
                // Use the latest record in the quarter
                const latestRecord = quarterRecords[0];
                const latestHours = parseFloat(latestRecord.dc_acft_flthrs) || 0;
                cumulativeHours = latestHours - baselineHours;
            } else if (matchingQtdRecords.length > 0) {
                // If no records in quarter but we have records, try summing all as incremental
                // This handles the case where records represent incremental hours
                cumulativeHours = matchingQtdRecords.reduce((sum, record) => {
                    const hours = parseFloat(record.dc_acft_flthrs) || 0;
                    return sum + hours;
                }, 0);
            }

            return {
                buno: aircraft.buno,
                site: aircraft.site,
                acoStatus: aircraft.status || 'N/A',
                cumulativeHours: cumulativeHours > 0 ? Math.round(cumulativeHours * 10) / 10 : 0,
                exemptStatus: exemptStatus[aircraft.buno] || 'MC',
                exemptDays: exemptDaysByBuno[aircraft.buno]?.exemptDays || 0,
                meets180DayRule: exemptDaysByBuno[aircraft.buno]?.meets180DayRule || false,
                historyGapAssumed: exemptDaysByBuno[aircraft.buno]?.historyGapAssumed || false
            };
        });
    }, [aircraftList, qtdAcTimeData, exemptStatus, exemptDaysByBuno]);

    // Calculate non-exempt average first (needed for SLMM Status calculation)
    const nonExemptAverage = useMemo(() => {
        const nonExemptAircraft = slmmData.filter(item => {
            const status = exemptStatus[item.buno] || 'MC';
            return status === 'MC';
        });

        if (nonExemptAircraft.length === 0) return 0;

        const total = nonExemptAircraft.reduce((sum, item) => {
            return sum + parseFloat(item.cumulativeHours) || 0;
        }, 0);

        const avg = total / nonExemptAircraft.length;
        return Math.round(avg * 10) / 10;
    }, [slmmData, exemptStatus]);

    // Add SLMM Status to each aircraft based on the formula
    const slmmDataWithStatus = useMemo(() => {
        return slmmData.map(item => {
            const exemptStat = exemptStatus[item.buno] || 'MC';
            const cumulativeHours = parseFloat(item.cumulativeHours) || 0;
            let slmmStatus = 'A';

            // If ACO Exempt Status is "SLMM", then SLMM Status is "SLMM"
            if (exemptStat === 'SLMM') {
                slmmStatus = 'SLMM';
            } else if (exemptStat === 'MC') {
                // Calculate thresholds based on average
                const threshold1 = nonExemptAverage * 1.1;
                const threshold2 = nonExemptAverage * 1.2;
                const threshold3 = nonExemptAverage * 1.4;

                if (cumulativeHours <= threshold1) {
                    slmmStatus = 'A';
                } else if (cumulativeHours > threshold1 && cumulativeHours <= threshold2) {
                    slmmStatus = 'B';
                } else if (cumulativeHours > threshold2 && cumulativeHours <= threshold3) {
                    slmmStatus = 'C';
                } else if (cumulativeHours > threshold3) {
                    slmmStatus = 'D';
                }
            }

            return {
                ...item,
                slmmStatus
            };
        });
    }, [slmmData, exemptStatus, nonExemptAverage]);

    // Filter and sort data
    const filteredData = useMemo(() => {
        let filtered = [...slmmDataWithStatus];

        // Apply site filter
        if (siteFilter) {
            filtered = filtered.filter(item => 
                item.site && item.site.toUpperCase() === siteFilter.toUpperCase()
            );
        }

        // Apply search filter
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(item =>
                item.buno.toLowerCase().includes(searchLower) ||
                item.acoStatus.toLowerCase().includes(searchLower) ||
                item.site.toLowerCase().includes(searchLower)
            );
        }

        return filtered.sort((a, b) => {
            // Sort by site first, then by BUNO
            if (a.site !== b.site) {
                return (a.site || '').localeCompare(b.site || '');
            }
            return (a.buno || '').localeCompare(b.buno || '');
        });
    }, [slmmDataWithStatus, siteFilter, searchTerm]);

    // Calculate summary statistics
    const summaryData = useMemo(() => {
        const data = filteredData;
        
        // Calculate average for non-exempt aircraft (needed for band thresholds)
        const nonExemptAircraft = data.filter(item => {
            const status = exemptStatus[item.buno] || 'MC';
            return status === 'MC';
        });
        
        const avgHours = nonExemptAircraft.length > 0
            ? nonExemptAircraft.reduce((sum, item) => {
                return sum + parseFloat(item.cumulativeHours) || 0;
            }, 0) / nonExemptAircraft.length
            : 0;

        // Calculate thresholds based on average (rounded to nearest tenth)
        const thresholdA = Math.round((avgHours * 1.1) * 10) / 10;
        const thresholdB = Math.round((avgHours * 1.2) * 10) / 10;
        const thresholdC = Math.round((avgHours * 1.4) * 10) / 10;

        // Count aircraft by SLMM Status (A, B, C, D) - only count MC aircraft
        const bandA = data.filter(item => {
            const status = exemptStatus[item.buno] || 'MC';
            return status === 'MC' && item.slmmStatus === 'A';
        }).length;
        
        const bandB = data.filter(item => {
            const status = exemptStatus[item.buno] || 'MC';
            return status === 'MC' && item.slmmStatus === 'B';
        }).length;
        
        const bandC = data.filter(item => {
            const status = exemptStatus[item.buno] || 'MC';
            return status === 'MC' && item.slmmStatus === 'C';
        }).length;
        
        const bandD = data.filter(item => {
            const status = exemptStatus[item.buno] || 'MC';
            return status === 'MC' && item.slmmStatus === 'D';
        }).length;

        // Calculate SLMMAA - count of aircraft with ACO Exempt Status = "MC"
        const slmmaa = data.filter(item => {
            const status = exemptStatus[item.buno] || 'MC';
            return status === 'MC';
        }).length;

        // Calculate SLMMESA - count of aircraft with ACO Exempt Status = "SLMM"
        const slmmesa = data.filter(item => {
            const status = exemptStatus[item.buno] || 'MC';
            return status === 'SLMM';
        }).length;

        const total = data.length;

        const nonExemptCount = nonExemptAircraft.length;

        // Calculate SLMM Average: (# of A's + (0.9 * # of B's) + (0.7 * # of C's)) / (# of SLMMAA + # of D's)
        const numerator = bandA + (0.9 * bandB) + (0.7 * bandC);
        const denominator = slmmaa + bandD;
        const slmmAverage = denominator > 0 ? ((numerator / denominator) * 100) : 0;

        return {
            bandA,
            bandB,
            bandC,
            bandD,
            slmmaa,
            slmmesa,
            total,
            nonExemptCount,
            nonExemptAverage: avgHours.toFixed(1),
            slmmAverage: slmmAverage.toFixed(2),
            thresholdA: thresholdA.toFixed(1),
            thresholdB: thresholdB.toFixed(1),
            thresholdC: thresholdC.toFixed(1)
        };
    }, [filteredData, exemptStatus]);

    return (
        <div style={styles.page}>
            <div style={styles.container}>
                <CustomAlert {...alert} onClose={() => setAlert({ show: false })} theme={theme} />
                
                <div style={styles.pageHeader}>
                    <HamburgerMenu theme={theme} />
                    <h1 style={styles.pageTitle}>SLMM - Service Life Maintenance Management</h1>
                    <div style={{ width: '20px' }}></div>
                </div>

                <div style={styles.header}>
                    <div>
                        <h1 style={{ margin: 0, color: styles.pageTitle.color }}>SLMM</h1>
                        {!isStaff && groups.length > 0 && (
                            <div style={{
                                fontSize: '14px',
                                color: styles.subtleText,
                                marginTop: '5px',
                                fontStyle: 'italic'
                            }}>
                                Showing aircraft for: {groups.join(', ')}
                            </div>
                        )}
                        {isStaff && (
                            <div style={{
                                fontSize: '14px',
                                color: styles.subtleText,
                                marginTop: '5px',
                                fontStyle: 'italic'
                            }}>
                                Showing all aircraft (Staff access)
                            </div>
                        )}
                    </div>
                </div>

                {/* Search and Filter Controls */}
                <div style={styles.controlsContainer}>
                    <input
                        type="text"
                        placeholder="Search by BUNO, Status, or Site..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={styles.searchInput}
                    />
                    <select
                        value={siteFilter}
                        onChange={(e) => setSiteFilter(e.target.value)}
                        style={styles.filterSelect}
                    >
                        <option value="">All Sites</option>
                        {Array.isArray(filteredSites) && filteredSites.map(site => (
                            <option key={site.id} value={site.name}>{site.name}</option>
                        ))}
                    </select>
                </div>

                {loading ? <Spinner /> : (
                    <div style={styles.summaryTableContainer}>
                        <div style={{ flex: 1, ...styles.tableContainer }}>
                            <table style={styles.table}>
                                <thead>
                                    <tr style={styles.tableHeader}>
                                        <th style={styles.tableHeaderCell}>BUNO</th>
                                        <th style={styles.tableHeaderCell}>Site</th>
                                        <th style={styles.tableHeaderCell}>ACO Status</th>
                                        <th style={styles.tableHeaderCell}>Cumulative Hours Flown In Quarter</th>
                                        <th style={styles.tableHeaderCell}>ACO Exempt Status</th>
                                        <th style={styles.tableHeaderCell}>Exempt Days (Option Year)</th>
                                        <th style={styles.tableHeaderCell}>SLMM Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredData.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" style={styles.noData}>
                                                {loading ? 'Loading...' : 'No aircraft data available.'}
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredData.map((item, index) => (
                                            <tr key={`${item.buno}-${index}`} style={styles.tableRow}>
                                                <td style={styles.tableCell}>
                                                    <span style={styles.buno}>{item.buno}</span>
                                                </td>
                                                <td style={styles.tableCell}>
                                                    <span style={styles.siteBadge}>{item.site}</span>
                                                </td>
                                                <td style={styles.tableCell}>
                                                    <span style={{
                                                        ...styles.statusBadge,
                                                        backgroundColor: (item.acoStatus && styles.status[item.acoStatus]) || '#6c757d'
                                                    }}>
                                                        {item.acoStatus}
                                                    </span>
                                                </td>
                                                <td style={styles.tableCell}>
                                                    {typeof item.cumulativeHours === 'number' ? item.cumulativeHours.toFixed(1) : parseFloat(item.cumulativeHours || 0).toFixed(1)} hrs
                                                </td>
                                                <td style={styles.tableCell}>
                                                    <select
                                                        value={item.exemptStatus}
                                                        onChange={(e) => handleExemptStatusChange(item.buno, e.target.value)}
                                                        style={{
                                                            padding: '4px 8px',
                                                            border: `1px solid ${styles.border}`,
                                                            borderRadius: '4px',
                                                            backgroundColor: item.exemptStatus === 'MC' 
                                                                ? (isDark ? '#1e3a5f' : '#b3d9ff') 
                                                                : (isDark ? '#3a3a3a' : '#d3d3d3'),
                                                            color: 'white',
                                                            fontSize: '14px',
                                                            cursor: 'pointer',
                                                            minWidth: '80px'
                                                        }}
                                                    >
                                                        <option value="MC" style={{ backgroundColor: isDark ? '#1e3a5f' : '#b3d9ff', color: 'white' }}>MC</option>
                                                        <option value="SLMM" style={{ backgroundColor: isDark ? '#3a3a3a' : '#d3d3d3', color: 'white' }}>SLMM</option>
                                                    </select>
                                                </td>
                                                <td style={styles.tableCell}>
                                                    <span style={{
                                                        ...styles.statusBadge,
                                                        backgroundColor: item.meets180DayRule ? '#28a745' : '#6c757d',
                                                        color: '#fff'
                                                    }}>
                                                        {item.exemptDays} days{item.historyGapAssumed ? ' *' : ''}
                                                    </span>
                                                </td>
                                                <td style={styles.tableCell}>
                                                    <span style={{
                                                        ...styles.statusBadge,
                                                        backgroundColor: item.slmmStatus === 'A' ? '#28a745' :
                                                                        item.slmmStatus === 'B' ? '#90ee90' :
                                                                        item.slmmStatus === 'C' ? '#ffc107' :
                                                                        item.slmmStatus === 'D' ? '#ff9800' :
                                                                        '#6c757d',
                                                        color: item.slmmStatus === 'B' || item.slmmStatus === 'C' ? '#000' : '#FFF'
                                                    }}>
                                                        {item.slmmStatus}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Summary Table */}
                        <div style={styles.summaryTable}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={styles.summaryTableHeader}>BAND</th>
                                        <th style={styles.summaryTableHeader}>A/C Count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr style={{ ...styles.summaryTableRow, ...styles.bandA }}>
                                        <td style={styles.summaryTableCell}>A: FLT HR &lt;= {summaryData.thresholdA}</td>
                                        <td style={styles.summaryTableCell}>{summaryData.bandA}</td>
                                    </tr>
                                    <tr style={{ ...styles.summaryTableRow, ...styles.bandB }}>
                                        <td style={{...styles.summaryTableCell, color: '#000'}}>B: FLT HR &lt;= {summaryData.thresholdB}</td>
                                        <td style={{...styles.summaryTableCell, color: '#000'}}>{summaryData.bandB}</td>
                                    </tr>
                                    <tr style={{ ...styles.summaryTableRow, ...styles.bandC }}>
                                        <td style={{...styles.summaryTableCell, color: '#000'}}>C: FLT HR &lt;= {summaryData.thresholdC}</td>
                                        <td style={{...styles.summaryTableCell, color: '#000'}}>{summaryData.bandC}</td>
                                    </tr>
                                    <tr style={{ ...styles.summaryTableRow, ...styles.bandD }}>
                                        <td style={styles.summaryTableCell}>D: FLT HR &gt; {summaryData.thresholdC}</td>
                                        <td style={styles.summaryTableCell}>{summaryData.bandD}</td>
                                    </tr>
                                    <tr style={styles.summaryTableRow}>
                                        <td style={styles.summaryTableCell}>SLMMAA</td>
                                        <td style={styles.summaryTableCell}>{summaryData.slmmaa}</td>
                                    </tr>
                                    <tr style={styles.summaryTableRow}>
                                        <td style={styles.summaryTableCell}>SLMMESA</td>
                                        <td style={styles.summaryTableCell}>{summaryData.slmmesa}</td>
                                    </tr>
                                    <tr style={{ ...styles.summaryTableRow, backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa', fontWeight: 'bold' }}>
                                        <td style={styles.summaryTableCell}>TOTAL A/C</td>
                                        <td style={styles.summaryTableCell}>{summaryData.total}</td>
                                    </tr>
                                </tbody>
                            </table>

                            {/* Non-Exempt Summary Table */}
                            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
                                <thead>
                                    <tr>
                                        <th style={styles.summaryTableHeader}>Metric</th>
                                        <th style={styles.summaryTableHeader}>Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr style={styles.summaryTableRow}>
                                        <td style={styles.summaryTableCell}>Non-Exempt Aircraft Count</td>
                                        <td style={styles.summaryTableCell}>{summaryData.nonExemptCount}</td>
                                    </tr>
                                    <tr style={styles.summaryTableRow}>
                                        <td style={styles.summaryTableCell}>Average Flight Hours (Non-Exempt)</td>
                                        <td style={styles.summaryTableCell}>{summaryData.nonExemptAverage} hrs</td>
                                    </tr>
                                    <tr style={styles.summaryTableRow}>
                                        <td style={styles.summaryTableCell}>SLMM Average</td>
                                        <td style={styles.summaryTableCell}>{summaryData.slmmAverage}%</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default SLMMPage;

